import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Zap, Brain, Search, Play, MessageSquare, Database } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function WhatsAppBasicLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Create your WhatsApp integration\n# Connect your personal WhatsApp to Memara\n\n# 2. Scan the QR code\n# Use your phone's camera to scan the connection code\n\n# 3. Start sending messages\n# Send any message to create memories instantly\n\n# 4. Search and organize\n# Find your captured memories in your Memara space`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };
  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=PPOb0_DMo7s', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="whatsapp_basic"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 shadow" aria-label="WhatsApp Logo">
          <Icon icon="logos:whatsapp-icon" width={48} height={48} />
        </span>
      }
      title="WhatsApp Memory Capture - Save Conversations to Your AI Memory"
      description="Transform your WhatsApp conversations into searchable memories that work across all your AI tools. Send any message to your connected WhatsApp number and instantly create memories that are available in CustomGPT, Claude Desktop, and all your other Memara integrations."
      features={[
        { icon: <Icon icon="logos:whatsapp-icon" width={24} height={24} />, title: 'Ultra-Fast 2-Minute Setup', description: 'Get started instantly with simple QR scan - no complex configuration or technical setup required for immediate memory capture.' },
        { icon: <Zap className="h-4 w-4 text-green-600" />, title: 'Instant Mobile Memory Creation', description: 'Every WhatsApp message becomes a searchable memory with real-time processing and cross-device synchronization.' },
        { icon: <Brain className="h-4 w-4 text-green-600" />, title: 'Intelligent Personal Organization', description: 'AI-powered automatic organization of personal memories with context understanding and smart categorization for personal knowledge management.' },
        { icon: <Search className="h-4 w-4 text-green-600" />, title: 'Advanced Personal Search', description: 'Find any memory instantly with semantic search, natural language queries, and intelligent context-aware retrieval across all conversations.' },
        { icon: <MessageSquare className="h-4 w-4 text-green-600" />, title: 'Cross-Platform Conversation Intelligence', description: 'Unified memory capture across WhatsApp Web, Desktop, and Mobile with intelligent conversation threading and context preservation.' },
        { icon: <Database className="h-4 w-4 text-green-600" />, title: 'Personal Knowledge Base', description: 'Build your personal intelligence repository with secure, private memory storage and multi-device access with privacy-first architecture.' },
      ]}
      setupSteps={[
        'Create your personal WhatsApp integration connection',
        'Scan the secure QR code with your mobile device',
        'Send any WhatsApp message to create searchable memories',
        'Access and search your personal knowledge base in Memara',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-green-600 hover:bg-green-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-green-200 text-green-600 hover:bg-green-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-green-200 text-green-600 hover:bg-green-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:whatsapp-icon" width={20} height={20} />,
        },
      ]}
      screenshots={[
        {
          id: 'whatsapp-qr-setup',
          title: 'QR Code Setup',
          description: 'Scan the secure QR code with your WhatsApp mobile app to connect instantly',
          placeholder: 'WhatsApp QR code scanning interface for Memara connection',
          badge: '2 min setup'
        },
        {
          id: 'message-to-memory',
          title: 'Message to Memory',
          description: 'Send any WhatsApp message and watch it transform into a searchable memory',
          placeholder: 'WhatsApp conversation showing message being converted to Memara memory',
          badge: 'Real-time'
        },
        {
          id: 'mobile-search',
          title: 'Mobile Memory Search',
          description: 'Search all your captured memories from any device with intelligent semantic search',
          placeholder: 'Memara mobile interface showing WhatsApp memory search results',
          badge: 'Cross-device'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=PPOb0_DMo7s"
      microcopy="Trusted by knowledge workers, researchers, and mobile-first professionals using WhatsApp for personal communication and instant memory capture on-the-go."
      trustedBy="WhatsApp, Personal Productivity Users, and more."
      testimonial="Memara's WhatsApp Basic integration transforms my mobile conversations into an intelligent personal knowledge system that captures every important insight, idea, and conversation with instant accessibility and perfect privacy."
    >
      {/* Custom content can go here if needed */}
    </IntegrationLandingBase>
  );
} 